import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Home, ScrollText, Briefcase, Scale, Shield, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "../components/ui/button";

const Services = () => {
  const [expanded, setExpanded] = useState(null);

  const scrollToSection = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const featuredService = {
    icon: Home,
    title: "Property Development and Specialist Conveyancing",
    subtitle: "From Land Acquisition to Final Transfer",
    description: "We provide specialist legal and conveyancing support throughout the property development lifecycle. Our role extends beyond conventional transfers to the structuring, registration and implementation of complex property developments.",
    items: [
      "Acquisition and transfer of development land",
      "Property and title-deed due diligence",
      "Analysis of title conditions, restrictions and existing servitudes",
      "Conveyancing support during township establishment",
      "Opening of township registers",
      "Registration of general plans and associated property rights",
      "First transfers from newly established townships",
      "Developer transfers to individual purchasers",
      "Consolidation and subdivision of properties",
      "Opening of sectional title registers",
      "Registration of sectional title units",
      "Phased sectional title developments",
      "Registration, amendment and cancellation of servitudes",
      "Removal or amendment of restrictive title conditions",
      "Property transfers involving companies, trusts and public institutions",
      "Rectification of title and registration discrepancies",
      "Coordination with developers, town planners, surveyors, municipalities, financiers and other professional teams",
    ],
    cta: "Explore Property Development",
  };

  const supportingServices = [
    {
      icon: ScrollText,
      tag: "Specialist",
      title: "Notarial Property and Mining Services",
      subtitle: "Creating and Protecting Registered Rights",
      description: "We prepare, execute and facilitate the registration of specialised notarial instruments affecting property, commercial security and mining-related rights.",
      items: [
        "Drafting and registration of long-term notarial leases",
        "Notarial leases for commercial, industrial and development projects",
        "Notarial deeds of servitude",
        "General and special notarial bonds",
        "Mining-related notarial deeds and agreements",
        "Notarial instruments connected to mining and prospecting rights",
        "Cessions, amendments and ancillary notarial instruments",
        "Notarial execution and registration support",
        "Authentication of documents for use outside South Africa",
        "Antenuptial and cohabitation agreements",
      ],
      cta: "Explore Notarial Services",
    },
    {
      icon: Briefcase,
      tag: "Specialist",
      title: "Commercial Agreements and Development Transactions",
      subtitle: "Legal Structures That Support Viable Projects",
      description: "We draft, review and negotiate the commercial agreements underpinning property developments, investment arrangements and long-term business relationships.",
      items: [
        "Concession and profit-sharing agreements",
        "Property development agreements",
        "Land availability and development arrangements",
        "Operator and management agreements",
        "Joint venture and partnership agreements",
        "Commercial lease agreements",
        "Sale of land, business and asset-sale agreements",
        "Service-level agreements",
        "Memoranda of understanding and cooperation agreements",
        "Shareholder and investment agreements",
        "Contractual due diligence and risk assessment",
        "Contract negotiation and transaction support",
      ],
      note: "Where a transaction involves a long-term lease requiring notarial execution or registration, our commercial and notarial expertise is integrated into one coordinated service.",
      cta: "Explore Commercial Agreements",
    },
    {
      icon: Scale,
      tag: "Complementary",
      title: "Civil Litigation for Property and Commercial Disputes",
      subtitle: "Protecting Rights and Resolving Complexity",
      description: "Our civil litigation practice focuses on disputes arising from property ownership, development projects, commercial agreements and contractual relationships.",
      items: [
        "Property ownership and title disputes",
        "Development and construction-related contractual disputes",
        "Breach and enforcement of commercial agreements",
        "Landlord and tenant disputes",
        "Eviction and unlawful occupation proceedings",
        "Interdicts and urgent court applications",
        "Debt and contractual recovery proceedings",
        "Declaratory and enforcement proceedings",
        "Settlement negotiations",
        "Mediation and alternative dispute resolution",
      ],
      cta: "Explore Dispute Resolution",
    },
    {
      icon: Shield,
      tag: "Complementary",
      title: "Corporate Governance and Board Advisory",
      subtitle: "Legal Insight for Responsible Decision Making",
      description: "We advise boards, committees, executives, companies and public institutions on governance responsibilities, legal risk and sound decision-making.",
      items: [
        "Board and committee advisory services",
        "Director duties and fiduciary responsibilities",
        "Board charters and committee terms of reference",
        "Governance frameworks and policies",
        "Delegations of authority",
        "Conflict-of-interest management",
        "Review of governance-related agreements",
        "Governance and legal risk assessments",
        "Legally informed support during investigations and grievances",
        "Governance advisory support for property and investment decisions",
      ],
      cta: "Explore Governance Advisory",
    },
  ];

  const toggleExpand = (idx) => setExpanded(expanded === idx ? null : idx);

  return (
    <section id="services" className="bg-light-grey py-24 lg:py-32" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-4">
            Our Legal Solutions
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl font-semibold text-charcoal leading-tight tracking-tighter max-w-3xl">
            Depth of Expertise Across the Property Lifecycle
          </h2>
        </motion.div>

        {/* Featured Service — Full Width */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-charcoal p-8 lg:p-12 mb-8 border border-charcoal"
          data-testid="featured-service"
        >
          <div className="flex items-center gap-3 mb-2">
            <featuredService.icon className="h-8 w-8 text-brand-gold" />
            <span className="text-brand-gold text-xs font-bold uppercase tracking-wider">Featured Legal Solution</span>
          </div>
          <h3 className="font-serif text-3xl sm:text-4xl text-white mb-2 tracking-tight">
            {featuredService.title}
          </h3>
          <p className="text-brand-gold/80 font-sans text-lg mb-6">{featuredService.subtitle}</p>
          <p className="text-white/70 font-sans text-base leading-relaxed mb-8 max-w-3xl">
            {featuredService.description}
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 mb-8">
            {featuredService.items.map((item, i) => (
              <div key={i} className="flex items-start text-white/60 text-sm font-sans">
                <span className="w-1.5 h-1.5 bg-brand-gold rounded-full mt-1.5 mr-2.5 flex-shrink-0" />
                <span>{item}</span>
              </div>
            ))}
          </div>
          <Button
            onClick={() => scrollToSection("#lead-capture")}
            className="btn-gold px-8 py-4 text-sm font-semibold uppercase tracking-wider rounded-none"
            data-testid="featured-service-cta"
          >
            {featuredService.cta}
          </Button>
        </motion.div>

        {/* Supporting Services — 2x2 Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {supportingServices.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white p-6 lg:p-8 border border-charcoal/10 group hover:border-brand-gold/30 transition-all"
              data-testid={`service-card-${index}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <service.icon className="h-6 w-6 text-brand-gold" />
                <span className={`text-xs font-bold uppercase tracking-wider ${service.tag === "Specialist" ? "text-brand-gold" : "text-charcoal/40"}`}>
                  {service.tag} Legal Solution
                </span>
              </div>
              <h3 className="font-serif text-xl text-charcoal mb-1 group-hover:text-brand-gold transition-colors">
                {service.title}
              </h3>
              <p className="text-charcoal/50 font-sans text-sm mb-3">{service.subtitle}</p>
              <p className="text-charcoal/60 font-sans text-sm leading-relaxed mb-4">
                {service.description}
              </p>

              {/* Expandable items */}
              <button
                onClick={() => toggleExpand(index)}
                className="flex items-center text-brand-gold text-xs font-semibold uppercase tracking-wider mb-3 hover:text-charcoal transition-colors"
                data-testid={`service-expand-${index}`}
              >
                {expanded === index ? "Show Less" : "View Services"}
                {expanded === index ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
              </button>

              <AnimatePresence>
                {expanded === index && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <ul className="space-y-2 mb-4">
                      {service.items.map((item, i) => (
                        <li key={i} className="flex items-start text-charcoal/60 font-sans text-sm">
                          <span className="w-1.5 h-1.5 bg-brand-gold rounded-full mt-1.5 mr-2 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    {service.note && (
                      <p className="text-charcoal/50 text-xs italic border-l-2 border-brand-gold/30 pl-3 mb-4">
                        {service.note}
                      </p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              <div
                onClick={() => scrollToSection("#lead-capture")}
                className="flex items-center text-brand-gold font-sans text-xs font-medium uppercase tracking-wider cursor-pointer pt-3 border-t border-charcoal/5"
              >
                <span>{service.cta}</span>
                <ArrowRight className="h-3 w-3 ml-2 group-hover:translate-x-2 transition-transform" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
